import { useCallback, useEffect, useRef, useState } from "react";
import {
    Badge,
    Box,
    CircularProgress,
    ClickAwayListener,
    IconButton,
    Paper,
    Popper,
    Typography,
} from "@mui/material";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import DefaultButton from "../buttons/DefaultButton";
import UserAvatar from "../UserAvatar";
import {
    fetchNotifications,
    markNotificationRead,
    NotificationDTO,
    NOTIFICATIONS_CHANGED_EVENT,
    notifyNotificationsChanged,
} from "../../../services/notifications";
import { notifyPlaylistsChanged, respondToContributorRequest } from "../../../services/playlist";
import { colors } from "../../../theme";

// Nothing pushes to the client - no socket, no SSE - so the only way to see a request
// someone else just filed is to ask. Long enough that idle tabs cost nothing, short
// enough that a request doesn't sit unseen: contributor requests are not urgent.
const POLL_MS = 30000;

function describe(notification: NotificationDTO): string {
    const actor = notification.actorUsername || "Someone";
    const playlist = notification.playlistName || "a playlist";
    switch (notification.type) {
        case "CONTRIBUTOR_REQUEST":
            return `${actor} wants to contribute to ${playlist}`;
        case "CONTRIBUTOR_REQUEST_APPROVED":
            return `${actor} approved your request to contribute to ${playlist}`;
        case "CONTRIBUTOR_REQUEST_DENIED":
            return `${actor} declined your request to contribute to ${playlist}`;
        case "PROMOTED_TO_CONTRIBUTOR":
            return `${actor} made you a contributor on ${playlist}`;
        default:
            return `${actor} did something on ${playlist}`;
    }
}

export default function NotificationBell() {
    const [items, setItems] = useState<NotificationDTO[]>([]);
    const [actionableCount, setActionableCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [open, setOpen] = useState(false);
    // Ids mid-request, so a row can't be clicked into two contradictory outcomes.
    const [pending, setPending] = useState<number[]>([]);
    const anchorRef = useRef<HTMLDivElement | null>(null);

    const load = useCallback(async () => {
        try {
            const response = await fetchNotifications();
            setItems(response.items);
            setActionableCount(response.actionableCount);
            setError(null);
        } catch (e) {
            setError(e instanceof Error ? e.message : "Couldn't load notifications");
        }
    }, []);

    useEffect(() => {
        setLoading(true);
        load().finally(() => setLoading(false));
    }, [load]);

    useEffect(() => {
        window.addEventListener(NOTIFICATIONS_CHANGED_EVENT, load);
        return () => window.removeEventListener(NOTIFICATIONS_CHANGED_EVENT, load);
    }, [load]);

    useEffect(() => {
        // A backgrounded tab has nobody looking at the badge, so skip those ticks and
        // catch up the moment it comes back to the front.
        const tick = () => {
            if (!document.hidden) {
                load();
            }
        };
        const timer = setInterval(tick, POLL_MS);
        document.addEventListener("visibilitychange", tick);
        return () => {
            clearInterval(timer);
            document.removeEventListener("visibilitychange", tick);
        };
    }, [load]);

    const handleRespond = async (notification: NotificationDTO, approve: boolean) => {
        setPending(prev => [...prev, notification.id]);
        try {
            await respondToContributorRequest(notification.playlistId, notification.actorUserId, approve);
            await load();
            notifyNotificationsChanged();
            // The requester's contributor flag changed, which the sidebar renders.
            notifyPlaylistsChanged();
        } catch (e) {
            setError(e instanceof Error ? e.message : "Couldn't respond to the request");
        } finally {
            setPending(prev => prev.filter(id => id !== notification.id));
        }
    };

    const handleMarkRead = async (notification: NotificationDTO) => {
        if (notification.read) {
            return;
        }
        setPending(prev => [...prev, notification.id]);
        try {
            await markNotificationRead(notification.id);
            await load();
        } catch (e) {
            setError(e instanceof Error ? e.message : "Couldn't update the notification");
        } finally {
            setPending(prev => prev.filter(id => id !== notification.id));
        }
    };

    return (
        <ClickAwayListener onClickAway={() => setOpen(false)}>
            <Box>
                <Box ref={anchorRef} sx={{ display: "flex" }}>
                    <IconButton
                        aria-label={`Notifications${actionableCount > 0 ? ` (${actionableCount} need action)` : ""}`}
                        onClick={() => setOpen(prev => !prev)}
                        sx={{
                            width: 38,
                            height: 38,
                            backgroundColor: colors.elevatedHover,
                            border: `1px solid ${colors.line}`,
                            "&:hover": { backgroundColor: colors.elevated },
                        }}
                    >
                        <Badge badgeContent={actionableCount} color="error" invisible={actionableCount === 0}>
                            <NotificationsNoneIcon sx={{ fontSize: 20, color: colors.textPrimary }} />
                        </Badge>
                    </IconButton>
                </Box>

                <Popper
                    open={open}
                    anchorEl={anchorRef.current}
                    placement="bottom-end"
                    style={{ zIndex: 1300, width: 360 }}
                >
                    <Paper
                        sx={{
                            mt: 0.75,
                            borderRadius: 2,
                            backgroundColor: colors.elevated,
                            border: `1px solid ${colors.line}`,
                            maxHeight: 420,
                            overflowY: "auto",
                        }}
                    >
                        <Typography sx={{ px: 2, py: 1.5, fontWeight: 700, fontSize: 14 }}>
                            Notifications
                        </Typography>

                        {loading && (
                            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                                <CircularProgress size={20} />
                            </Box>
                        )}

                        {error && (
                            <Typography sx={{ px: 2, pb: 1.5, fontSize: 13, color: colors.danger }}>
                                {error}
                            </Typography>
                        )}

                        {!loading && items.length === 0 && !error && (
                            <Typography sx={{ px: 2, pb: 2, fontSize: 13, color: colors.textSecondary }}>
                                Nothing here yet.
                            </Typography>
                        )}

                        {items.map(notification => {
                            // A settled request stays in the list as history but must stop
                            // offering Approve/Deny - the server would reject a second
                            // answer, and the row is no longer a decision to make.
                            const isRequest = notification.type === "CONTRIBUTOR_REQUEST" && !notification.read;
                            const busy = pending.includes(notification.id);
                            return (
                                <Box
                                    key={notification.id}
                                    onClick={() => { if (!isRequest) { handleMarkRead(notification); } }}
                                    sx={{
                                        display: "flex",
                                        gap: 1.25,
                                        px: 2,
                                        py: 1.25,
                                        borderTop: `1px solid ${colors.line}`,
                                        cursor: isRequest || notification.read ? "default" : "pointer",
                                        opacity: notification.read ? 0.55 : 1,
                                        "&:hover": {
                                            backgroundColor: isRequest ? "transparent" : colors.elevatedHover,
                                        },
                                    }}
                                >
                                    <UserAvatar username={notification.actorUsername} size={32} />
                                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                                        <Typography sx={{ fontSize: 13, lineHeight: 1.4 }}>
                                            {describe(notification)}
                                        </Typography>
                                        {isRequest && (
                                            <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                                                <DefaultButton
                                                    text="Approve"
                                                    size="small"
                                                    disabled={busy}
                                                    onClick={() => handleRespond(notification, true)}
                                                />
                                                <DefaultButton
                                                    text="Deny"
                                                    variant="secondary"
                                                    size="small"
                                                    disabled={busy}
                                                    onClick={() => handleRespond(notification, false)}
                                                />
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            );
                        })}
                    </Paper>
                </Popper>
            </Box>
        </ClickAwayListener>
    );
}
